import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { apiErr, fmtDate, fmtQ } from "@/merenda/common";
import { WeeklyChart } from "@/merenda/WeeklyChart";

const List = ({ title, items, render, id }) => (
  <div className="mb-3" data-testid={id}>
    <h4 className="mb-1 text-sm font-bold text-slate-700">{title} ({items.length})</h4>
    {items.length ? <ul className="space-y-0.5 text-sm text-slate-600">{items.map((x, i) => <li key={i}>• {render(x)}</li>)}</ul> : <p className="text-xs text-slate-400">Nada a registrar.</p>}
  </div>
);

const Detail = ({ w }) => {
  const d = w.data;
  return (
    <Card className="border-slate-200 p-4" data-testid="weekly-detail">
      <div className="mb-3 text-sm text-slate-500">{d.merenda_days.length} dia(s) de merenda · {d.meals_served} refeições · gerado por {w.generated_by} em {new Date(w.updated_at).toLocaleString("pt-BR")}</div>
      <WeeklyChart chart={d.chart} />
      <List id="weekly-alerts" title={`Perto de acabar (< ${fmtQ(d.alert_limit)} dias)`} items={d.alertas} render={(a) => <><b>{a.name}</b>: {fmtQ(a.balance)} {a.unit}, ~{fmtQ(a.coverage_days)} dia(s)</>} />
      <List id="weekly-divergences" title="Divergências real x média (> 15%)" items={d.divergencias} render={(x) => <><b>{x.name}</b>: real {fmtQ(x.qty)} {x.unit}, esperado {fmtQ(x.expected)} ({x.diff_pct > 0 ? "+" : ""}{fmtQ(x.diff_pct)}%)</>} />
      <List id="weekly-pending" title="Dias sem consumo confirmado" items={d.pending_days} render={fmtDate} />
      <List id="weekly-nopresence" title="Dias sem presença registrada" items={d.no_presence_days} render={fmtDate} />
      <List id="weekly-consumo" title="Consumo da semana" items={d.consumo} render={(c) => `${c.name}: ${fmtQ(c.qty)} ${c.unit}`} />
      <List id="weekly-losses" title="Perdas" items={d.perdas} render={(c) => `${c.name}: ${fmtQ(c.qty)} ${c.unit}`} />
      <List id="weekly-adjust" title="Ajustes de inventário" items={d.ajustes} render={(c) => `${c.name}: ${fmtQ(c.qty)} ${c.unit}`} />
    </Card>
  );
};

export const ResumosSemanais = ({ scopeParams }) => {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/merenda/weekly", { params: scopeParams }).then((r) => { setList(r.data); setSel((s) => r.data.find((x) => x.id === s?.id) || r.data[0] || null); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const gen = async () => {
    setBusy(true);
    try { await api.post("/merenda/weekly/generate", {}, { params: scopeParams }); toast.success("Resumo da semana gerado"); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const pdf = async (w) => {
    const { data } = await api.get(`/merenda/weekly/${w.id}/pdf`, { params: scopeParams, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `resumo_merenda_${w.week_start}.pdf`; a.click();
  };
  return (
    <div data-testid="weekly-tab">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button onClick={gen} disabled={busy} data-testid="weekly-generate"><RefreshCw className={`mr-1 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Gerar resumo desta semana</Button>
        {sel && <Button variant="outline" onClick={() => pdf(sel)} data-testid="weekly-pdf"><FileDown className="mr-1 h-4 w-4" /> PDF</Button>}
        <span className="text-xs text-slate-500">Enviado automaticamente toda sexta às 17h (notificação no sino da direção).</span>
      </div>
      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <div className="space-y-2">{list.map((w) => (
          <button key={w.id} onClick={() => setSel(w)} className={`w-full rounded-lg border p-3 text-left text-sm ${sel?.id === w.id ? "border-orange-400 bg-orange-50" : "border-slate-200 bg-white"}`} data-testid={`weekly-item-${w.id}`}>
            <b>{fmtDate(w.week_start)} – {fmtDate(w.week_end)}</b>
            <div className="mt-1 flex flex-wrap gap-1">
              {w.data.alertas.length > 0 && <Badge className="bg-red-100 text-red-700">{w.data.alertas.length} alerta(s)</Badge>}
              {w.data.divergencias.length > 0 && <Badge className="bg-amber-100 text-amber-800">{w.data.divergencias.length} diverg.</Badge>}
              {w.data.pending_days.length > 0 && <Badge className="bg-slate-200 text-slate-700">{w.data.pending_days.length} pend.</Badge>}
            </div>
          </button>))}
          {!list.length && <p className="text-sm text-slate-400">Nenhum resumo ainda.</p>}
        </div>
        {sel && <Detail w={sel} />}
      </div>
    </div>
  );
};
